import {useState} from 'react';
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '../components/card';
import {Button} from '../components/button';
import {Input} from '../components/input';
import {Label} from '../components/label';
import {Separator} from '../components/separator';
import {Badge} from '../components/badge';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger
} from '../components/alert-dialog';
import {User as UserIcon, Mail, Phone, Shield, Trash2, Save} from 'lucide-react';
import {User} from '../models/User';
import {toast} from 'sonner';
import {useNavigate} from 'react-router-dom';
import {UserService} from "../services/UserService";
import {wait} from "@testing-library/user-event/dist/utils";

interface UserProfileProps {
    user: User;
    onLogout: () => void;
}

export function UserProfile({user, onLogout}: UserProfileProps) {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNumber: user.phoneNumber
    });
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
    });

    const handleChange = (field: string, value: string) => {
        setFormData(prev => ({...prev, [field]: value}));
    };

    const handleSave = async () => {
        try {
            await UserService.updateCurrentUser(formData);
            user = await UserService.getCurrentUser();
            toast.success('Profil mis à jour avec succès');
        } catch (error) {
            toast.error('Erreur lors de la mise à jour du profil');
        }
    };

    const handleDeleteAccount = async () => {
        try {
            await UserService.deleteCurrentUser();
            toast.success('Compte supprimé');
            localStorage.removeItem('token');
            await wait(2000);
            onLogout();
            navigate('/login');
        } catch (error) {
            toast.error('Erreur lors de la suppression du compte');
        }
    };

    const handleChangePassword = async () => {
        try {
            if (passwordData.newPassword !== passwordData.confirmNewPassword) {
                toast.error('Les nouveaux mots de passe ne correspondent pas');
            } else {
                await UserService.updateCurrentUser({
                    password: passwordData.currentPassword,
                    newPassword: passwordData.newPassword
                });
                navigate('/dashboard');
            }
        } catch (error) {
            toast.error('Erreur lors du changement de mot de passe');
        }
    }

    return (
        <div className="space-y-6 flex flex-col justify-center items-center mx-auto p-4">
            <div className="w-full max-w-2xl">
                <h1>Mon profil</h1>
                <p className="text-muted-foreground">
                    Gérez vos informations personnelles et vos préférences
                </p>
            </div>

            {/* Profile Overview */}
            <Card className="w-full max-w-2xl p-4">
                <div className="flex items-center gap-4">
                    <div className="h-20 w-20 rounded-full bg-primary flex items-center justify-center">
              <span className="text-3xl text-primary-foreground">
                {user.firstName[0]}{user.lastName[0]}
              </span>
                    </div>
                    <div className="flex flex-col gap-4">
                        <CardTitle>{user.firstName} {user.lastName}</CardTitle>
                        <CardDescription className="flex items-center gap-2 mt-1">
                            <Badge variant={user.role === 'manager' ? 'default' : 'secondary'}>
                                {user.role === 'manager' ? 'Manager' : 'Employé'}
                            </Badge>
                        </CardDescription>
                    </div>
                </div>
            </Card>

            {/* Personal Information */}
            <Card className="w-full max-w-2xl">
                <CardHeader>
                    <CardTitle>Informations personnelles</CardTitle>
                    <CardDescription>
                        Modifiez vos informations de compte
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="firstName">Prénom</Label>
                            <div className="relative">
                                <UserIcon
                                    className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"/>
                                <Input
                                    id="firstName"
                                    value={formData.firstName}
                                    onChange={(e) => handleChange('firstName', e.target.value)}
                                    className="pl-10"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="lastName">Nom</Label>
                            <div className="relative">
                                <UserIcon
                                    className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"/>
                                <Input
                                    id="lastName"
                                    value={formData.lastName}
                                    onChange={(e) => handleChange('lastName', e.target.value)}
                                    className="pl-10"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"/>
                            <Input
                                id="email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleChange('email', e.target.value)}
                                className="pl-10"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="phone">Téléphone</Label>
                        <div className="relative">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"/>
                            <Input
                                id="phone"
                                type="tel"
                                value={formData.phoneNumber}
                                onChange={(e) => handleChange('phoneNumber', e.target.value)}
                                className="pl-10"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end pt-4">
                        <Button onClick={handleSave} className="gap-2">
                            <Save className="h-4 w-4"/>
                            Enregistrer les modifications
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Security */}
            <Card className="w-full max-w-2xl">
                <CardHeader>
                    <CardTitle>Sécurité</CardTitle>
                    <CardDescription>
                        Gérez la sécurité de votre compte
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center gap-3">
                            <Shield className="h-5 w-5 text-muted-foreground"/>
                            <div>
                                <p>Mot de passe</p>
                                <p className="text-muted-foreground">
                                    Dernière modification il y a 2 mois
                                </p>
                            </div>
                        </div>
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <Button variant="outline">Modifier</Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogTitle>
                                    Modifier le mot de passe
                                </AlertDialogTitle>
                                <AlertDialogHeader>
                                    <Input
                                        type="password"
                                        placeholder="Mot de passe actuel"
                                        value={passwordData.currentPassword}
                                        onChange={(e) => setPasswordData({
                                            ...passwordData,
                                            currentPassword: e.target.value
                                        })}
                                        className="mb-4"
                                    />
                                    <Input
                                        type="password"
                                        placeholder="Nouveau mot de passe"
                                        value={passwordData.newPassword}
                                        onChange={(e) => setPasswordData({
                                            ...passwordData,
                                            newPassword: e.target.value
                                        })}
                                        className="mb-4"
                                    />
                                    <Input
                                        type="password"
                                        placeholder="Confirmer le nouveau mot de passe"
                                        value={passwordData.confirmNewPassword}
                                        onChange={(e) => setPasswordData({
                                            ...passwordData,
                                            confirmNewPassword: e.target.value
                                        })}
                                    />
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                                    <AlertDialogAction
                                        onClick={handleChangePassword}
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                        Modifier le mot de passe
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </div>
                </CardContent>
            </Card>

            {/* Danger Zone */}
            <Card className="border-destructive w-full max-w-2xl">
                <CardHeader>
                    <CardTitle className="text-destructive">Zone dangereuse</CardTitle>
                    <CardDescription>
                        Actions irréversibles sur votre compte
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Separator className="mb-4"/>
                    <div className="flex items-center justify-between">
                        <div>
                            <p>Supprimer mon compte</p>
                            <p className="text-muted-foreground">
                                Cette action est définitive et irréversible
                            </p>
                        </div>
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <Button variant="destructive" className="gap-2">
                                    <Trash2 className="h-4 w-4"/>
                                    Supprimer le compte
                                </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Êtes-vous absolument sûr?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        Cette action est irréversible. Cela supprimera définitivement votre compte
                                        et toutes vos données de nos serveurs.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                                    <AlertDialogAction
                                        onClick={handleDeleteAccount}
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                        Oui, supprimer mon compte
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
